import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, LifeBuoy, Send, HelpCircle, CheckCircle2, AlertCircle } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import { sendSupportMessage } from '../services/supportService';

const SUBJECT_OPTIONS = [
  'Consulta general',
  'Problema con una cita',
  'Problema con un pago',
  'Problema técnico',
  'Otro'
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
  name: '',
  email: '',
  subject: 'Consulta general',
  message: ''
};

function SupportPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverMessage, setServerMessage] = useState('');

  // El canonical apunta siempre a /soporte: la ruta /contacto sirve esta misma
  // página y sin esta señal Google la trataría como contenido duplicado.
  useSeoMeta(
    'Soporte y contacto | DocEnLinea',
    '¿Necesitas ayuda con una cita, un pago o un problema técnico en DocEnLinea? Escríbenos desde este formulario y nuestro equipo de soporte te responde.',
    '/soporte'
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'El nombre es obligatorio.';
    if (!form.email.trim()) next.email = 'El correo es obligatorio.';
    else if (!EMAIL_REGEX.test(form.email.trim())) next.email = 'El correo no es válido.';
    if (!form.message.trim()) next.message = 'El mensaje es obligatorio.';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMessage('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('loading');
    try {
      const res = await sendSupportMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject,
        message: form.message.trim()
      });
      setStatus('success');
      setServerMessage(res?.message || 'Hemos recibido tu mensaje. Te responderemos pronto.');
      setForm(INITIAL_FORM);
      setErrors({});
    } catch (err) {
      setStatus('error');
      setServerMessage(
        err?.response?.data?.message ||
          'No pudimos enviar tu mensaje. Intenta de nuevo más tarde.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Navbar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/login" className="btn-secondary">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn-primary">
                Registrarse
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Contenido */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <LifeBuoy className="h-12 w-12 text-primary-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Soporte y Contacto</h1>
          <p className="text-lg text-gray-600">
            ¿Tienes una pregunta o necesitas ayuda? Escríbenos y te responderemos pronto.
          </p>
        </div>

        {/* Ayuda rápida: enlace a FAQ */}
        <div className="card mb-8 flex items-start gap-3">
          <HelpCircle className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
          <div>
            <p className="font-medium text-gray-900">Antes de escribirnos</p>
            <p className="text-gray-600">
              Muchas dudas se resuelven en nuestra{' '}
              <Link to="/faq" className="text-primary-600 font-medium hover:underline">
                página de Preguntas Frecuentes
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Estado de éxito / error */}
        {status === 'success' && (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800"
            role="status"
          >
            <CheckCircle2 className="h-6 w-6 flex-shrink-0" />
            <span>{serverMessage}</span>
          </div>
        )}
        {status === 'error' && (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"
            role="alert"
          >
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
            <span>{serverMessage}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="card space-y-5" noValidate>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className="input-field"
              placeholder="Tu nombre completo"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Correo electrónico <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className="input-field"
              placeholder="tucorreo@ejemplo.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Asunto
            </label>
            <select
              id="subject"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              className="input-field"
            >
              {SUBJECT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Mensaje <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              className="input-field"
              placeholder="Cuéntanos en qué podemos ayudarte..."
            />
            {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
          </div>

          <button
            type="submit"
            disabled={status === 'loading'}
            className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Send className="h-5 w-5" />
            {status === 'loading' ? 'Enviando...' : 'Enviar'}
          </button>
        </form>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold">DocEnLinea</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <Link to="/faq" className="hover:text-white">Preguntas Frecuentes</Link>
            <Link to="/soporte" className="hover:text-white">Soporte</Link>
            <Link to="/login" className="hover:text-white">Iniciar Sesión</Link>
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 DocEnLinea. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default SupportPage;
